import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { BadgeCounts } from '@biyolink/shared';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/api/auth';
import { useBadgeCounts } from '@/api/notifications';
import { ApprovalBadge, Badge, Button, toast } from '@/components/ui';
import { FacilitySwitcher } from '@/components/panel/FacilitySwitcher';
import { NotificationBell } from '@/components/panel/NotificationBell';
import { cn } from '@/lib/cn';
import { BrandMark } from '@/components/layout/BrandMark';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { t } from '@/i18n';
import type { CompanyType } from '@biyolink/shared';

interface NavItem {
  to: string;
  label: string;
  /** Sonraki fazlarda açılacak bölümler devre dışı gösterilir. */
  disabled?: boolean;
  /** Okunmamış sayacı gösterilecek bölüm. */
  badge?: keyof BadgeCounts;
}

/*
 * Etiketler ÇAĞRI ANINDA üretiliyor.
 *
 * Modül seviyesinde `t()` çağıran bir sabit, import edildiği an bir kez hesaplanıp
 * donuyordu: dil değiştirilince bu metinler eski dilde kalıyordu. Rotalar `React.lazy`
 * ile bölündüğü için sonuç tutarsız da oluyordu — dil değişiminden önce yüklenmiş
 * sayfa eski, sonra yüklenen yeni dilde geliyordu.
 */
function navItems(type: CompanyType | undefined): NavItem[] {
  const satabilir = type === 'SELLER' || type === 'BOTH';

  return [
    { to: '/panel', label: t('nav.dashboard') },
    // İlan yalnız satıcının işi; alıcı ilan vermiyor.
    ...(satabilir ? [{ to: '/panel/ilanlar', label: t('nav.myListings') }] : []),
    // Teklif ekranı alıcıdan kalktı: cevabı biz mesajla veriyoruz (docs/12).
    ...(satabilir
      ? [{ to: '/panel/teklifler', label: t('nav.offers'), badge: 'offers' as const }]
      : []),
    { to: '/panel/numuneler', label: t('nav.samples') },
    { to: '/panel/siparisler', label: t('nav.myOrders') },
    // Favoriler 14.09'da menüden kalkmıştı; müşteri "ekleme var, listeleme yok" deyince
    // geri geldi (12.10.2026). Kayıtlı aramalar hâlâ menüde yok (rota duruyor).
    { to: '/panel/favoriler', label: t('nav.favorites') },
    { to: '/panel/firma', label: t('nav.companyProfile') },
    { to: '/panel/tesisler', label: t('nav.facilities') },
    { to: '/panel/ayarlar', label: t('nav.settings') },
    // Günlük iş değil takip ekranları: listenin altında duruyorlar.
    { to: '/panel/mesajlar', label: t('nav.messages'), badge: 'messages' as const },
    { to: '/panel/bildirimler', label: t('nav.notifications'), badge: 'notifications' as const },
  ];
}

/** Girişli kullanıcı alanının çerçevesi: sol sidebar + breadcrumb (docs/05 §2). */
export function PanelLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const company = useAuthStore((state) => state.company);
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const badges = useBadgeCounts();

  // Alt yollarda da (ör. /panel/teklifler/:id) üst bölümün adı gösterilir.
  const currentLabel =
    navItems(company?.type)
      .filter((item) => location.pathname.startsWith(item.to) && item.to !== '/panel')
      .sort((a, b) => b.to.length - a.to.length)[0]?.label ?? t('nav.panel');

  async function handleLogout() {
    await logout.mutateAsync().catch(() => undefined);
    toast.success(t('auth.logoutSuccess'));
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="border-b border-slate-200 bg-surface">
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={t('nav.panel')}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            >
              <span aria-hidden="true" className="block text-lg leading-none">
                ☰
              </span>
            </button>

            <Link to="/" className="rounded-lg" aria-label={t('app.name')}>
              <BrandMark showSuffix={false} />
            </Link>

            {/*
              Panelden vitrine dönüş: başlıkta doğrudan bağlantı. Önceden tek çıkış
              logoydu, o da ana sayfaya gidiyordu — kullanıcı ilan listesine dönmek için
              önce ana sayfaya uğramak zorunda kalıyordu.
            */}
            <nav aria-label={t('nav.marketplace')} className="hidden items-center gap-4 md:flex">
              <Link
                to="/ilanlar"
                className="text-sm font-medium text-slate-600 hover:text-primary-900"
              >
                {t('nav.listings')}
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-slate-900">{user?.fullName}</p>
              <p className="text-xs text-slate-500">{company?.name}</p>
            </div>
            <FacilitySwitcher />
            <NotificationBell />
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={handleLogout} isLoading={logout.isPending}>
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="container-page flex flex-1 gap-8 py-6">
        <aside className={cn('w-full shrink-0 lg:block lg:w-56', menuOpen ? 'block' : 'hidden')}>
          <nav className="space-y-1" aria-label={t('nav.panel')}>
            {navItems(company?.type).map((item) =>
              item.disabled ? (
                <span
                  key={item.to}
                  aria-disabled="true"
                  title={t('panel.comingSoon')}
                  className="block cursor-not-allowed rounded-xl px-3 py-2 text-sm text-slate-400"
                >
                  {item.label}
                </span>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/panel'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-medium transition',
                      isActive ? 'bg-primary-800 text-white' : 'text-slate-700 hover:bg-slate-100',
                    )
                  }
                >
                  {item.label}
                  {item.badge && (badges.data?.[item.badge] ?? 0) > 0 && (
                    <Badge tone="warning">{badges.data?.[item.badge]}</Badge>
                  )}
                </NavLink>
              ),
            )}
          </nav>

          {/* Vitrine dönüş kenar çubuğunda da dursun: mobilde başlıktaki bağlantı gizli. */}
          <div className="mt-4 border-t border-slate-200 pt-4">
            <Link
              to="/ilanlar"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-primary-900"
            >
              <span aria-hidden="true">←</span>
              {t('nav.backToMarketplace')}
            </Link>
          </div>

          {company && (
            <div className="mt-6 rounded-card border border-slate-200 bg-surface p-4">
              <p className="text-xs text-slate-500">{t('nav.companyProfile')}</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{company.name}</p>
              <div className="mt-2">
                <ApprovalBadge status={company.approvalStatus} />
              </div>
            </div>
          )}
        </aside>

        <main className={cn('min-w-0 flex-1', menuOpen && 'hidden lg:block')}>
          <nav aria-label="breadcrumb" className="mb-4 text-sm text-slate-500">
            <Link to="/panel" className="hover:text-primary-900">
              {t('nav.panel')}
            </Link>
            {currentLabel !== t('nav.panel') && (
              <>
                <span className="mx-2" aria-hidden="true">
                  /
                </span>
                <span className="text-slate-700">{currentLabel}</span>
              </>
            )}
          </nav>

          <Outlet />
        </main>
      </div>
    </div>
  );
}
